import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "../../../../api/axios";
import { 
  TruckIcon,
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from "@heroicons/react/24/outline";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export const SupplierInvoicesTab = ({ project, canEdit = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSupplierInvoices = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/projects/${project.id}/supplier_invoices`);
        if (response.data.success) {
          setSupplierInvoices(response.data.data || []);
        } else {
          setError("Failed to load supplier invoices");
        }
      } catch (err) {
        console.error("Error fetching supplier invoices:", err);
        setError("Failed to load supplier invoices");
      } finally {
        setLoading(false);
      }
    };

    if (project?.id) {
      fetchSupplierInvoices();
    }
  }, [project?.id]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "pending":
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case "overdue":
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <TruckIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved":
        return t("project.supplier_invoices.status.approved") || "Godkjent";
      case "pending":
        return t("project.supplier_invoices.status.pending") || "Venter godkjenning";
      case "overdue":
        return t("project.supplier_invoices.status.overdue") || "Forfalt";
      case "paid":
        return t("project.supplier_invoices.status.paid") || "Betalt";
      case "not_approved":
        return t("project.supplier_invoices.status.not_approved") || "Ikke godkjent";
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK'
    }).format(amount);
  };

  // Calculate totals for a group of postings, excluding payment entries
  // Same logic as in detail.js
  const calculatePostingTotals = (postings) => {
    if (!postings || postings.length === 0) {
      return { amount: 0, amount_gross: 0, vat: 0 };
    }
    
    // Filter out payment postings - we want expense postings and VAT postings
    const expensePostings = postings.filter((posting) => {
      const amountGross = parseFloat(posting.amount_gross || 0);
      const accountNum = posting.account_number || posting.account_id?.toString() || "";
      
      // Exclude negative amounts (payments)
      if (amountGross < 0) return false;
      
      // Exclude payment accounts (2400 = Leverandørgjeld, 1920 = Bankinnskudd)
      if (accountNum === "2400" || accountNum.startsWith("1920")) return false;
      
      return true;
    });
    
    // Separate expense postings from VAT postings
    const actualExpensePostings = expensePostings.filter((p) => {
      const accountNum = p.account_number || p.account_id?.toString() || "";
      return !accountNum.startsWith("27");
    });
    
    const vatPostings = expensePostings.filter((p) => {
      const accountNum = p.account_number || p.account_id?.toString() || "";
      return accountNum.startsWith("27");
    });
    
    // Calculate totals
    const expenseAmountExclVat = actualExpensePostings.reduce(
      (sum, posting) => sum + parseFloat(posting.amount || 0),
      0
    );
    
    const vatFromExpenses = actualExpensePostings.reduce(
      (sum, posting) => {
        const amount = parseFloat(posting.amount || 0);
        const amountGross = parseFloat(posting.amount_gross || 0);
        return sum + (amountGross - amount);
      },
      0
    );
    
    const deductibleVat = vatPostings.reduce(
      (sum, posting) => sum + parseFloat(posting.amount || 0),
      0
    );
    
    // amount_excluding_vat = expense postings (excl. VAT) - this is the actual expense amount before VAT deduction
    // This represents what was actually spent, not the net cost after VAT deduction
    const amountExclVat = expenseAmountExclVat;
    // Total including VAT (for display) = expense postings (excl. VAT) + VAT on expenses
    const amountGross = expenseAmountExclVat + vatFromExpenses;
    
    return {
      amount: amountExclVat, // Expense amount excluding VAT (before deduction)
      amount_gross: amountGross, // Total expenses including VAT
      vat: vatFromExpenses, // VAT on expenses
      deductible_vat: deductibleVat, // Deductible VAT (for reference)
      net_cost: expenseAmountExclVat - deductibleVat, // Net cost after VAT deduction (for reference)
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.supplier_invoices.error.title") || "Feil ved lasting"}
        </h3>
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.supplier_invoices.overview")}
        </h3>
        {canEdit ? (
        <button
          onClick={() => {/* TODO: Implement create supplier invoice */}}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t("project.supplier_invoices.create")}
        </button>
        ) : null}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TruckIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Totalt for prosjekt
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(supplierInvoices.reduce((sum, inv) => sum + (inv.project_amount_excluding_vat || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Godkjent
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(supplierInvoices.filter(inv => inv.approved).reduce((sum, inv) => sum + (inv.project_amount_excluding_vat || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Venter godkjenning
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(supplierInvoices.filter(inv => !inv.approved && !inv.not_approved).reduce((sum, inv) => sum + (inv.project_amount_excluding_vat || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Ikke godkjent
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(supplierInvoices.filter(inv => inv.not_approved).reduce((sum, inv) => sum + (inv.project_amount_excluding_vat || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Invoices Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fakturanummer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leverandør
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Beskrivelse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Totalt faktura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Beløp for prosjekt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Forfallsdato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mottatt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {supplierInvoices.map((invoice) => {
                // Calculate invoice totals from postings (same logic as detail.js)
                // This ensures correct calculation even if backend values are incorrect
                const invoicePostings = invoice.postings || [];
                const invoiceTotals = calculatePostingTotals(invoicePostings);
                
                // Use calculated values from postings, fallback to backend values if no postings
                const invoiceAmountExcludingVat = invoiceTotals.amount || Number(invoice.amount_excluding_vat) || 0;
                const invoiceVat = invoiceTotals.vat || Number(invoice.vat) || 0;
                const totalInvoiceAmount = invoiceTotals.amount_gross || Number(invoice.amount) || 0;
                
                // Currency values (if available)
                const invoiceAmountCurrency = Number(invoice.amount_currency) || 0;
                const invoiceAmountExcludingVatCurrency = Number(invoice.amount_excluding_vat_currency) || 0;
                
                // Project-specific totals - ensure all values are numbers
                const projectAmount = Number(invoice.project_amount) || 0;
                const projectAmountCurrency = Number(invoice.project_amount_currency) || 0;
                const projectAmountExcludingVat = Number(invoice.project_amount_excluding_vat) || projectAmount;
                const projectAmountExcludingVatCurrency = Number(invoice.project_amount_excluding_vat_currency) || 0;
                const projectVat = Number(invoice.project_vat) || 0;
                const projectTotal = Number(invoice.project_total) || 0;
                const hasMultipleProjects = invoice.project_postings && invoice.project_postings.length > 0 && totalInvoiceAmount !== projectTotal;
                
                return (
                  <React.Fragment key={invoice.id}>
                    <tr className={hasMultipleProjects ? "bg-blue-50" : ""}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <TruckIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {invoice.invoice_number || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.supplier?.name || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>
                          {invoice.description || "-"}
                          {invoice.project_postings && invoice.project_postings.length > 0 && (
                            <div className="mt-1 text-xs text-gray-500">
                              {invoice.project_postings.length} posting{invoice.project_postings.length !== 1 ? 'er' : ''} for dette prosjektet
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{formatCurrency(totalInvoiceAmount)}</div>
                          <div className="text-xs text-gray-500">
                            Ekskl. mva: {formatCurrency(invoiceAmountExcludingVat)}
                          </div>
                          {invoiceVat > 0 && (
                            <div className="text-xs text-gray-500">
                              Mva: {formatCurrency(invoiceVat)}
                            </div>
                          )}
                          {invoiceAmountCurrency > 0 && invoiceAmountCurrency !== invoiceAmountExcludingVat && (
                            <div className="text-xs text-gray-400 mt-1">
                              Valuta: {formatCurrency(invoiceAmountCurrency)}
                            </div>
                          )}
                          {invoiceAmountExcludingVatCurrency > 0 && invoiceAmountExcludingVatCurrency !== invoiceAmountExcludingVat && (
                            <div className="text-xs text-gray-400">
                              Ekskl. mva (valuta): {formatCurrency(invoiceAmountExcludingVatCurrency)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium text-blue-700">{formatCurrency(projectAmountExcludingVat)}</div>
                          {projectVat > 0 && (
                            <div className="text-xs text-gray-500">
                              Mva: {formatCurrency(projectVat)}
                            </div>
                          )}
                          {projectTotal > projectAmountExcludingVat && (
                            <div className="text-xs text-gray-400">
                              Inkl. mva: {formatCurrency(projectTotal)}
                            </div>
                          )}
                          {projectVat > 0 && (
                            <div className="text-xs text-gray-500">
                              Mva: {formatCurrency(projectVat)}
                            </div>
                          )}
                          {projectAmountCurrency > 0 && projectAmountCurrency !== projectAmountExcludingVat && (
                            <div className="text-xs text-gray-400 mt-1">
                              Valuta: {formatCurrency(projectAmountCurrency)}
                            </div>
                          )}
                          {projectAmountExcludingVatCurrency > 0 && projectAmountExcludingVatCurrency !== projectAmountExcludingVat && (
                            <div className="text-xs text-gray-400">
                              Ekskl. mva (valuta): {formatCurrency(projectAmountExcludingVatCurrency)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice.approved ? "bg-green-100 text-green-800" :
                          invoice.not_approved ? "bg-red-100 text-red-800" :
                          "bg-yellow-100 text-yellow-800"
                        }`}>
                          {invoice.approved ? <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" /> :
                           invoice.not_approved ? <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mr-1" /> :
                           <ClockIcon className="h-4 w-4 text-yellow-500 mr-1" />}
                          <span>{invoice.approved ? "Godkjent" : invoice.not_approved ? "Ikke godkjent" : "Venter"}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.expiration_date ? new Date(invoice.expiration_date).toLocaleDateString('no-NO') : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('no-NO') : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => navigate(`/dashboard/supplier-invoices/${invoice.id}`)}
                          className="text-blue-600 hover:text-blue-900" 
                          title="Vis detaljer"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                    {invoice.project_postings && invoice.project_postings.length > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan="9" className="px-6 py-3">
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={`postings-content-${invoice.id}`}
                              id={`postings-header-${invoice.id}`}
                            >
                              <div className="text-xs font-medium text-gray-700">
                                Postings for dette prosjektet ({invoice.project_postings.length})
                              </div>
                            </AccordionSummary>
                            <AccordionDetails>
                              <div className="space-y-1">
                                {invoice.project_postings.map((posting, idx) => {
                                  // Ensure all posting values are numbers
                                  const postingAmount = Number(posting.amount) || 0;
                                  const postingAmountExcludingVat = Number(posting.amount_excluding_vat) || postingAmount;
                                  const postingAmountCurrency = Number(posting.amount_currency) || 0;
                                  const postingAmountExcludingVatCurrency = Number(posting.amount_excluding_vat_currency) || 0;
                                  const postingVat = Number(posting.vat) || 0;
                                  const postingTotal = postingAmountExcludingVat + postingVat;
                                  
                                  return (
                                    <div key={idx} className="flex justify-between items-center text-xs text-gray-600 bg-white p-2 rounded">
                                      <div className="flex-1">
                                        <span className="font-medium">{posting.description || "Ingen beskrivelse"}</span>
                                        {posting.date && (
                                          <span className="ml-2 text-gray-500">
                                            ({new Date(posting.date).toLocaleDateString('no-NO')})
                                          </span>
                                        )}
                                        {posting.vat_type && (
                                          <span className="ml-2 text-gray-400">
                                            (MVA: {posting.vat_type.display_name || posting.vat_type.name || (posting.vat_type.percentage ? posting.vat_type.percentage + '%' : '')})
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <div className="font-medium">{formatCurrency(postingAmountExcludingVat)}</div>
                                        {postingVat > 0 && (
                                          <div className="text-gray-500">+ mva: {formatCurrency(postingVat)}</div>
                                        )}
                                        <div className="text-gray-500">Totalt: {formatCurrency(postingTotal)}</div>
                                        {postingAmountCurrency > 0 && postingAmountCurrency !== postingAmountExcludingVat && (
                                          <div className="text-gray-400 mt-1">Valuta: {formatCurrency(postingAmountCurrency)}</div>
                                        )}
                                        {postingAmountExcludingVatCurrency > 0 && postingAmountExcludingVatCurrency !== postingAmountExcludingVat && (
                                          <div className="text-gray-400">Ekskl. mva (valuta): {formatCurrency(postingAmountExcludingVatCurrency)}</div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {supplierInvoices.length === 0 && (
          <div className="text-center py-12">
            <TruckIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ingen leverandørfakturaer
            </h3>
            <p className="text-gray-500 mb-4">
              Ingen leverandørfakturaer er registrert for dette prosjektet ennå
            </p>
            {canEdit ? (
            <button
              onClick={() => {/* TODO: Implement create supplier invoice */}}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t("project.supplier_invoices.create")}
            </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
